import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CheckCircle, ChevronRight, Crosshair, Zap } from 'lucide-react';

const ProfileFieldsGrid = ({ fields, dataSource }) => {
    if (!fields || !dataSource) return null;
    return (
        <div className="grid grid-cols-2 gap-3 mb-4">
            {fields.map(pf => {
                const Icon = pf.icon;
                const val = dataSource[pf.key];
                if (!val) return null;
                return (
                    <div key={pf.key} className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                        <div className="flex items-center gap-1.5 mb-1">
                            <Icon className="h-3.5 w-3.5" style={{ color: pf.color }} />
                            <p className="text-xs font-semibold uppercase" style={{ color: pf.color }}>{pf.label}</p>
                        </div>
                        <p className="text-xs text-[#1F2A30]">{val}</p>
                    </div>
                );
            })}
        </div>
    );
};

const Chapter9Fields = ({ fields, profile }) => {
    if (!fields) return null;
    return (
        <div className="grid grid-cols-2 gap-3 mb-4">
            {fields.map(pf => {
                const Icon = pf.icon;
                const raw = profile?.[pf.key];
                if (!raw) return null;
                const val = typeof raw === 'object'
                    ? Object.values(raw).filter(Boolean).join(' | ')
                    : raw;
                return (
                    <div key={pf.key} className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8] col-span-2">
                        <div className="flex items-center gap-1.5 mb-1">
                            <Icon className="h-3.5 w-3.5" style={{ color: pf.color }} />
                            <p className="text-xs font-semibold uppercase" style={{ color: pf.color }}>{pf.label}</p>
                        </div>
                        <p className="text-xs text-[#1F2A30] line-clamp-3">{val}</p>
                    </div>
                );
            })}
        </div>
    );
};

const Chapter3Insights = ({ daiCard, lighthouseTask }) => {
    if (!daiCard && !lighthouseTask) return null;
    return (
        <div className="grid grid-cols-2 gap-3 mb-4">
            {lighthouseTask && (
                <div className="p-3 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                    <div className="flex items-center gap-1.5 mb-1">
                        <Crosshair className="h-3.5 w-3.5 text-[#5FAF9D]" />
                        <p className="text-xs font-semibold text-[#5FAF9D] uppercase">Tarea faro</p>
                    </div>
                    <p className="text-xs text-[#1F2A30]">{lighthouseTask}</p>
                </div>
            )}
            {daiCard?.primera_accion && (
                <div className="p-3 bg-[#E8B27A]/5 rounded-xl border border-[#E8B27A]/20">
                    <div className="flex items-center gap-1.5 mb-1">
                        <Zap className="h-3.5 w-3.5 text-[#E8B27A]" />
                        <p className="text-xs font-semibold text-[#E8B27A] uppercase">DAI</p>
                    </div>
                    <p className="text-xs text-[#1F2A30]">{daiCard.primera_accion}</p>
                </div>
            )}
        </div>
    );
};

const HabitFieldsGrid = ({ fields, habitPlan }) => {
    if (!fields || !habitPlan) return null;
    return (
        <div className="grid grid-cols-2 gap-3 mb-4">
            {fields.map(hf => {
                const Icon = hf.icon;
                const val = habitPlan[hf.key];
                if (!val) return null;
                return (
                    <div key={hf.key} className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                        <div className="flex items-center gap-1.5 mb-1">
                            <Icon className="h-3.5 w-3.5" style={{ color: hf.color }} />
                            <p className="text-xs font-semibold uppercase" style={{ color: hf.color }}>{hf.label}</p>
                        </div>
                        <p className="text-xs text-[#1F2A30]">{val}</p>
                    </div>
                );
            })}
        </div>
    );
};

export default function ChapterCard({ chapter, chIdx, isModuleCompleted, profile }) {
    const completed = chapter.moduleIds.filter(id => isModuleCompleted(id)).length;
    const total = chapter.moduleIds.length;
    const pct = Math.round((completed / total) * 100);
    const isComplete = pct === 100;
    const isActive = completed > 0 && !isComplete;

    const radiografia = profile?.radiografia_perfil;
    const daiCard = profile?.dai_card;
    const lighthouseTask = profile?.lighthouse_task;
    const habitPlan = profile?.habit_plan;

    return (
        <div className="relative lg:pl-20" data-testid={`chapter-evolution-${chapter.num}`}>
            {/* Timeline dot */}
            <div className={`absolute left-4 lg:left-5 w-8 h-8 rounded-full flex items-center justify-center z-10 shadow-md transition-all ${
                isComplete ? 'bg-[#5FAF9D]' : isActive ? `bg-[${chapter.color}]` : 'bg-[#DCE3E8]'
            }`} style={{ backgroundColor: isComplete ? '#5FAF9D' : isActive ? chapter.color : '#DCE3E8' }}>
                {isComplete ? (
                    <CheckCircle className="h-5 w-5 text-white" />
                ) : (
                    <span className={`text-sm font-bold ${isActive ? 'text-white' : 'text-[#7A8A93]'}`}>{chapter.num}</span>
                )}
            </div>

            <Card className={`bg-white border shadow-sm transition-all ${isComplete ? 'border-[#5FAF9D]/30 shadow-[#5FAF9D]/10' : isActive ? 'border-[#3E5F6E]/20' : 'border-[#DCE3E8] opacity-70'}`}>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: isComplete ? '#5FAF9D' : chapter.color }}>
                                    Capítulo {chapter.num}
                                </span>
                                {isComplete && <span className="text-xs bg-[#5FAF9D]/10 text-[#5FAF9D] px-2 py-0.5 rounded-full font-medium">Completado</span>}
                                {isActive && <span className="text-xs bg-[#E8B27A]/10 text-[#E8B27A] px-2 py-0.5 rounded-full font-medium">En progreso</span>}
                            </div>
                            <CardTitle className="text-lg text-[#1F2A30] font-['Outfit']">{chapter.title}</CardTitle>
                            <p className="text-sm text-[#7A8A93]">{chapter.subtitle}</p>
                        </div>
                        <div className="text-right">
                            <span className="text-3xl font-bold font-['Outfit']" style={{ color: isComplete ? '#5FAF9D' : chapter.color }}>{pct}%</span>
                            <p className="text-xs text-[#7A8A93]">{completed}/{total}</p>
                        </div>
                    </div>
                    {/* Module dots */}
                    <div className="flex gap-1.5 mt-3">
                        {chapter.moduleIds.map((modId) => (
                            <div key={modId} className={`h-2 flex-1 rounded-full ${isModuleCompleted(modId) ? 'bg-[#5FAF9D]' : 'bg-[#DCE3E8]'}`} />
                        ))}
                    </div>
                </CardHeader>

                {isComplete && (
                    <CardContent className="pt-0">
                        {chapter.num === 2 && radiografia && (
                            <ProfileFieldsGrid fields={chapter.profileFields} dataSource={radiografia} />
                        )}
                        {chapter.num === 3 && (
                            <Chapter3Insights daiCard={daiCard} lighthouseTask={lighthouseTask} />
                        )}
                        {chapter.num === 4 && (
                            <HabitFieldsGrid fields={chapter.habitFields} habitPlan={habitPlan} />
                        )}
                        {chapter.num === 9 && (
                            <Chapter9Fields fields={chapter.profileFields} profile={profile} />
                        )}

                        <Link to={chapter.summaryPath}>
                            <Button variant="ghost" className="text-[#5FAF9D] hover:bg-[#5FAF9D]/5 px-0" data-testid={`view-summary-ch${chapter.num}`}>
                                Ver resumen completo <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </Link>
                    </CardContent>
                )}
            </Card>
        </div>
    );
}
